import React from "react";
import { useReactToPrint } from "react-to-print";
import { formatDate } from "@/utils/format-date";
import { Badge } from "@/components/ui/badge";

interface Process {
  id: number;
  uuid: string;
  quota_quantity: number;
  unity: {
    id: number;
    name: string;
  };
  fromUnity: {
    id: number;
    name: string;
  } | null;
  seller: {
    id: number;
    user: {
      id: number;
      name: string;
      email: string;
    };
  } | null;
  fromSeller: {
    id: number;
    user: {
      id: number;
      name: string;
      email: string;
    };
  } | null;
  distributionType: {
    id: number;
    name: string;
  };
  status: {
    id: number;
    name: string;
  };
  created_at: string;
  processed_at: string;
  createdBy: {
    id: number;
    name: string;
  };
  updatedBy: {
    id: number;
    name: string;
  };
}

interface DistributionReceiptData {
  cp_classification_id: number;
  product_name: string;
  contest: {
    id: number;
    name: string;
    validation_date: string;
    prize: number;
  };
  lottery: {
    id: number;
    name: string;
    color: string;
  };
  product_classification: {
    id: number;
    name: string;
    quota_quantity: number;
  };
  total_quantity: number;
  total_processes: number;
  processes: Process[];
}

export const DistributionReceipt = ({
  data,
}: {
  data: DistributionReceiptData;
}) => {
  const componentRef = React.useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    documentTitle: `Comprovante Distribuição - ${data.product_name}`,
    pageStyle: `
      @page {
        size: 80mm auto;
        margin: 5mm 2mm;
      }
      @media print {
        body {
          background: none !important;
          font-size: 12px !important;
          color: #000000 !important;
        }
        .receipt-container {
          box-shadow: none !important;
          padding: 8px !important;
          width: 76mm !important;
          max-width: 76mm !important;
          background-size: 20px 20px !important;
          background: none !important;
          color: #000000 !important;
        }
        .no-print {
          display: none !important;
        }
        * {
          color: #000000 !important;
          background: transparent !important;
          border-color: #000000 !important;
        }
      }
    `,
    contentRef: componentRef,
  });

  // Agrupar por destinatário
  const groupedByRecipient = data.processes.reduce((acc, process) => {
    let recipientName = "";
    if (process.seller) {
      recipientName = process.seller.user.name;
    } else if (process.unity) {
      recipientName = process.unity.name;
    } else {
      recipientName = "Sem destinatário";
    }

    if (!acc[recipientName]) {
      acc[recipientName] = [];
    }
    acc[recipientName].push(process);
    return acc;
  }, {} as Record<string, Process[]>);

  const getStatusColor = (statusName: string) => {
    if (statusName.toLowerCase().includes("aceito")) {
      return "bg-green-500";
    } else if (statusName.toLowerCase().includes("recusado")) {
      return "bg-red-500";
    }
    return "bg-yellow-500";
  };

  return (
    <div className="w-full max-w-sm mx-auto">
      <button
        onClick={() => handlePrint()}
        className="mb-4 bg-[#023859] text-white px-6 py-2 rounded-md hover:bg-[#034872] transition-colors no-print"
        type="button"
      >
        Imprimir Comprovante
      </button>

      <div
        ref={componentRef}
        className="receipt-container bg-[#FFFBEB] p-4 shadow-lg relative w-full max-w-[300px]"
        style={{
          backgroundImage: 'url("/images/receipt-bg.svg")',
          backgroundSize: "30px 30px",
          minHeight: "auto",
        }}
      >
        <div className="flex flex-col items-center mb-4 section-print">
          <img
            src="/images/logo-color.png"
            alt="Loteria Aldeota"
            className="h-16 mb-2 logo-print"
          />
        </div>

        <div className="text-center mb-4 section-print">
          <h2 className="text-lg font-bold uppercase text-[#023859] title-print">
            {data.product_name}
          </h2>
          <p className="text-[#023859] mt-1 text-sm text-print">
            {data.product_classification.name}
          </p>
        </div>

        <div className="bg-[#F4A340] text-white py-3 px-3 rounded-lg mb-4 section-print">
          <h3 className="text-center text-sm font-bold title-print">
            DETALHES DA DISTRIBUIÇÃO
          </h3>
          <div className="mt-2 text-xs">
            <p className="text-center text-print">Concurso: {data.contest.name}</p>
            <p className="text-center text-print">
              Loteria: {data.lottery.name}
            </p>
            <p className="text-center text-print">
              Data: {formatDate(data.contest.validation_date)}
            </p>
            <div className="mt-3 bg-white/30 p-2 rounded">
              <p className="text-center font-bold">
                Total: {data.total_quantity} cotas
              </p>
              <p className="text-center text-xs">
                {data.total_processes} distribuições
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          {Object.entries(groupedByRecipient).map(
            ([recipientName, processes]) => {
              const totalQuantity = processes.reduce(
                (sum, p) => sum + p.quota_quantity,
                0
              );
              const acceptedCount = processes.filter((p) =>
                p.status.name.toLowerCase().includes("aceito")
              ).length;
              const rejectedCount = processes.filter((p) =>
                p.status.name.toLowerCase().includes("recusado")
              ).length;

              return (
                <div
                  key={recipientName}
                  className="bg-white/50 p-3 rounded-lg section-print"
                >
                  <p className="font-bold text-[#023859] text-sm mb-2">
                    Para: {recipientName}
                  </p>

                  {processes.map((process) => (
                    <div
                      key={process.id}
                      className="mt-2 bg-white/70 p-2 rounded border-l-4"
                      style={{
                        borderLeftColor: process.status.name
                          .toLowerCase()
                          .includes("aceito")
                          ? "#10b981"
                          : process.status.name.toLowerCase().includes("recusado")
                          ? "#ef4444"
                          : "#eab308",
                      }}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="text-xs text-[#023859]">
                            {process.distributionType.name}
                          </p>
                          <p className="text-xs text-[#023859] mt-1">
                            Cotas: {process.quota_quantity}
                          </p>
                        </div>
                        <Badge
                          className={`text-xs ${getStatusColor(
                            process.status.name
                          )} text-white`}
                        >
                          {process.status.name}
                        </Badge>
                      </div>
                      <div className="mt-2 text-xs text-[#023859]">
                        <p>Criado por: {process.createdBy.name}</p>
                        <p>Em: {formatDate(process.created_at)}</p>
                        {process.processed_at && (
                          <p>
                            Processado:{" "}
                            {formatDate(process.processed_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="mt-2 pt-2 border-t border-[#023859]">
                    <div className="flex justify-between text-xs text-[#023859]">
                      <span>Total para {recipientName}:</span>
                      <span className="font-bold">{totalQuantity} cotas</span>
                    </div>
                    {acceptedCount > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ {acceptedCount} aceito{acceptedCount > 1 ? "s" : ""}
                      </p>
                    )}
                    {rejectedCount > 0 && (
                      <p className="text-xs text-red-600">
                        ✗ {rejectedCount} recusado{rejectedCount > 1 ? "s" : ""}
                      </p>
                    )}
                  </div>
                </div>
              );
            }
          )}
        </div>

        <div className="mt-6 pt-4 section-print">
          <div className="w-full border-t border-[#023859] pt-2">
            <p className="text-center text-[#023859] font-medium text-xs text-print">
              COMPROVANTE DE DISTRIBUIÇÃO
            </p>
          </div>
        </div>

        <div className="mt-4 flex justify-center">
          <img
            src="/images/rei-do-bolao.png"
            alt="Rei do Bolão"
            className="h-12 footer-logo-print"
          />
        </div>
      </div>
    </div>
  );
};
